import React, { useMemo } from 'react';
import { Box, MenuItem, Select, Typography } from '@mui/material';
import colors from 'src/styles/equalution/colors';
import Fonts from 'src/styles/equalution/fonts';
import { useAppSelector } from 'src/store/hooks';

type Props = {
  value: string | null;
  handleChange: (value: string) => void;
  disabled?: boolean;
};

// Platform detection to determine if the user is on a mobile device
const isMobilePlatform = () => {
  if (typeof navigator === 'undefined') return false;

  // Check user agent for mobile devices OR viewport width for small screens
  const isMobileByUserAgent = /Android|iPhone|iPad|iPod/i.test(
    navigator.userAgent
  );
  const isMobileByViewport =
    typeof window !== 'undefined' && window.innerWidth <= 768;

  return isMobileByUserAgent || isMobileByViewport;
};

export const ProteinFlavorField: React.FC<Props> = ({
  value,
  handleChange,
  disabled,
}) => {
  const isMobile = isMobilePlatform();
  const shopifyVariants = useAppSelector(state => state.signUp.shopifyVariants);

  // Dynamic options: only variants with is_protein_flavour === 1 AND is_included === 1
  const options: string[] = useMemo(() => {
    if (!shopifyVariants || shopifyVariants.length === 0) {
      return [];
    }

    return shopifyVariants
      .filter(
        variant => variant.is_protein_flavour === 1 && variant.is_included === 1
      )
      .map(variant => variant.variant_name);
  }, [shopifyVariants]);

  return (
    <Box
      sx={{ marginRight: '12px', width: '100%' }}
      display="flex"
      flexDirection="column"
      alignSelf="flex-start"
      gap={2}>
      <Typography
        sx={{
          fontSize: '20px',
          fontWeight: 700,
          color: colors.OFF_BLACK,
          textAlign: 'left',
        }}>
        Select Protein Flavour
      </Typography>

      {/* ---------- Mobile: Native Select ---------- */}
      {isMobile ? (
        <Box
          component="select"
          value={value ?? ''}
          disabled={disabled}
          onChange={(e: any) => handleChange(e.target.value as string)}
          sx={{
            maxWidth: 330,
            padding: '14px 12px',
            fontSize: '16px',
            borderRadius: '4px',
            border: '1px solid #ccc',
            backgroundColor: colors.WHITE,
            appearance: 'auto',
            fontFamily: Fonts.FontType.regular,
          }}>
          <option value="" disabled>
            Select
          </option>

          {options.map(flavor => (
            <option key={flavor} value={flavor}>
              {flavor}
            </option>
          ))}
        </Box>
      ) : (
        /* ---------- Desktop: MUI Select ---------- */
        <Select
          value={value ?? ''}
          onChange={e => handleChange(e.target.value as string)}
          displayEmpty
          disabled={disabled}
          sx={{
            maxWidth: 330,
            backgroundColor: colors.WHITE,
          }}>
          <MenuItem value="" disabled>
            Select
          </MenuItem>

          {options.map(flavor => (
            <MenuItem key={flavor} value={flavor} sx={{ padding: 2 }}>
              {flavor}
            </MenuItem>
          ))}
        </Select>
      )}
    </Box>
  );
};
